import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQueryClient } from '@/api/api-client';

import { ApiTagsEnum } from '@/enum/apiTags.enum';

import {
  CLIENT_CODE,
  GAMEZONE_TILES_URL,
  TENANT_CODE,
} from '@/utils/constants';

import {
  GetGameZoneContestRes,
  GetGameZoneTemplateRes,
  GetUserProfileRes,
  GetUpcomingContestsRes,
  GetUserContestsRes,
  LoginUserBody,
  LoginWithTokenRes,
  ResetPasswordBody,
  ResetPasswordRes,
  SignInRes,
  SignUpRes,
  UpdateUserDataSubscriptionBody,
  UpdateUserProfileBody,
  UserRegisterBody,
  LimitOfset,
} from '@/types/domain';

export const api = createApi({
  reducerPath: 'mainApi',
  tagTypes: Object.values(ApiTagsEnum),
  baseQuery: baseQueryClient,
  endpoints: (builder) => ({
    getGameZoneTemplate: builder.query<GetGameZoneTemplateRes, void>({
      query: () => ({
        url: `${GAMEZONE_TILES_URL}/clients/${CLIENT_CODE}/gamezone-template`,
        params: {
          tenant: TENANT_CODE,
        },
      }),
      providesTags: [ApiTagsEnum.GET_GAME_ZONE_TEMPLATE],
    }),
    getUpcomingContests: builder.query<GetUpcomingContestsRes, void>({
      query: () => ({
        url: '/contests',
        params: {
          limit: 4,
          offset: 0,
          tenant: TENANT_CODE,
        },
      }),
    }),
    getGameHistory: builder.query<GetUserContestsRes, void>({
      query: () => ({
        url: '/user/games-history',
        params: {
          limit: 4,
          offset: 0,
        },
      }),
    }),
    getUpcomingHistory: builder.query<GetUserContestsRes, void>({
      query: () => ({
        url: '/user/games-upcoming-history',
        params: {
          limit: 5,
          offset: 0,
        },
      }),
    }),
    getRecentHistory: builder.query<GetUserContestsRes, LimitOfset>({
      query: ({ limit = 5 }) => ({
        url: '/user/games-recent-history',
        params: {
          limit,
          offset: 0,
        },
      }),
    }),
    signIn: builder.mutation<SignInRes, LoginUserBody>({
      query: (body) => ({
        url: '/auth/login',
        body,
        method: 'POST',
      }),
      invalidatesTags: [ApiTagsEnum.GET_USER_PROFILE],
    }),
    signUp: builder.mutation<SignUpRes, UserRegisterBody>({
      query: (body) => ({
        url: '/auth/signup',
        body,
        method: 'POST',
      }),
    }),
    generateTmpToken: builder.mutation<{ token: string }, void>({
      query: () => ({
        url: '/auth/tmp-token',
        method: 'POST',
      }),
    }),
    loginWithToken: builder.mutation<LoginWithTokenRes, { token: string }>({
      query: (body) => ({
        url: '/auth/login-with-token',
        method: 'POST',
        body,
      }),
      invalidatesTags: [ApiTagsEnum.GET_USER_PROFILE],
    }),
    requestResetLink: builder.mutation<{ success: boolean }, { email: string }>(
      {
        query: (body) => ({
          url: '/auth/forgot-password',
          method: 'POST',
          body,
        }),
      },
    ),
    resetPassword: builder.mutation<ResetPasswordRes, ResetPasswordBody>({
      query: (body) => ({
        url: '/auth/renew-password',
        method: 'POST',
        body,
      }),
    }),
    updateUserDataSubscription: builder.mutation<
      { success: boolean },
      UpdateUserDataSubscriptionBody
    >({
      query: (body) => ({
        url: '/user/notification-preferences',
        method: 'PUT',
        body,
      }),
    }),
    getUserProfile: builder.query<GetUserProfileRes, void>({
      query: () => ({
        url: '/user/me',
      }),
      providesTags: [ApiTagsEnum.GET_USER_PROFILE],
    }),
    updateUserProfile: builder.mutation<
      { success: boolean },
      UpdateUserProfileBody
    >({
      query: (body) => ({
        url: '/user',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [ApiTagsEnum.GET_USER_PROFILE],
    }),
    getGameZoneContest: builder.query<GetGameZoneContestRes, { url: string }>({
      query: ({ url }) => ({ url }),
    }),
  }),
});

export const {
  useGetGameZoneTemplateQuery,
  useGetUpcomingContestsQuery,
  useGetUpcomingHistoryQuery,
  useGetRecentHistoryQuery,
  useSignInMutation,
  useSignUpMutation,
  useGenerateTmpTokenMutation,
  useLoginWithTokenMutation,
  useRequestResetLinkMutation,
  useResetPasswordMutation,
  useUpdateUserDataSubscriptionMutation,
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useLazyGetGameZoneContestQuery,
} = api;
